import { useCallback } from 'react'

import { isValidArray, tr } from 'mmfintech-commons'
import { extractBankName, extractBankNameDomestic } from 'mmfintech-checkout-commons'

import { Slide } from 'react-awesome-reveal'
import { BankIcon } from './BankIcon'
import { SelectBankItem } from '@components/SelectBankPane/SelectBankItem'

import type { SupportedBank, UseFilteredBanksOutput } from 'mmfintech-checkout-commons/types'

import './selectBankPane.scss'

type SelectBankPaneProps = {
  onContinue: () => void
  selectedBankId: string
  banksLoaded: boolean
  filteredBanks: UseFilteredBanksOutput
  loading?: boolean
}

const SelectBankPane = (props: SelectBankPaneProps) => {
  const { onContinue, selectedBankId, banksLoaded, filteredBanks, loading } = props

  const handleBankClick = useCallback(
    (bank: SupportedBank): void => {
      if (selectedBankId === bank.bankChoiceId) {
        onContinue()
      } else {
        filteredBanks.handleSelectBank(bank)
      }
    },
    [selectedBankId, filteredBanks, onContinue]
  )

  if (!banksLoaded) {
    return null
  }

  if (filteredBanks.banks?.length || !filteredBanks.treeMode) {
    return (
      <div className='select-bank-wrapper'>
        <div className='select-bank-container'>
          {filteredBanks.hasBack && (
            <SelectBankItem
              back
              selected
              data-test='banks-back'
              onClick={filteredBanks.handleBack}
              caption={extractBankName(filteredBanks.selectedGroup)}
              description={extractBankNameDomestic(filteredBanks.selectedGroup)}
              loading={loading}
            />
          )}

          {isValidArray<SupportedBank[]>(filteredBanks.banks) ? (
            <Slide
              direction='right'
              cascade
              damping={0.05}
              duration={filteredBanks.selectedGroup ? 500 : 0}
              triggerOnce
            >
              {filteredBanks.banks.map((bank: SupportedBank) => (
                <SelectBankItem
                  key={bank.bankChoiceId}
                  caption={extractBankName(bank)}
                  description={extractBankNameDomestic(bank)}
                  selected={selectedBankId === bank.bankChoiceId}
                  onClick={() => handleBankClick(bank)}
                  icon={<BankIcon bank={bank} />}
                  data-test={`bank-${bank.bankChoiceId}`}
                  loading={loading}
                  maintenance={bank.underMaintenance}
                  className={filteredBanks.hasBack ? 'bank-inner' : ''}
                />
              ))}
            </Slide>
          ) : !filteredBanks.treeMode ? (
            <div className='select-bank-error-wrapper' data-test='error-text'>
              {filteredBanks.bankSearchText?.length > 2
                ? tr('CHECKOUT.PAYMENT.NO_BANKS_FOUND', 'No banks found')
                : tr('CHECKOUT.PAYMENT.SEARCH_BANK_HINT', 'Type 3 or more characters to filter banks…')}
            </div>
          ) : null}
        </div>
      </div>
    )
  }

  if (filteredBanks.treeMode) {
    return (
      <div className='select-bank-error-wrapper no-margin' data-test='error-text'>
        {tr('CHECKOUT.PAYMENT.NO_BANKS.MESSAGE', 'No banks available')}
      </div>
    )
  }

  return null
}

export default SelectBankPane
